/** Custom event types and their JSON Schemas (M15-31, M15-43). */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { Alert, Button, Card, EmptyState, Field, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, inputClass } from '../../components/ui';
import { EVENT_TONE, errorText } from './shared';

export interface EventType { id: string; code: string; description: string; granularity: 'EACH' | 'DAILY'; active: boolean; currentVersion: number; schema: object; versions: { version: number; createdAt: string }[]; events: Record<string, number> }

const SAMPLE_SCHEMA = {
  type: 'object',
  required: ['gross_sales', 'tenders'],
  properties: {
    branch: { type: 'string' },
    gross_sales: { type: 'string', pattern: '^\\d+(\\.\\d{1,2})?$' },
    tenders: { type: 'array', items: { type: 'object', required: ['method', 'amount'], properties: { method: { type: 'string' }, amount: { type: 'string' } } } },
  },
};

export function useEventTypes() {
  return useQuery({ queryKey: ['event-types'], queryFn: () => api<EventType[]>('/event-types') });
}

function EventTypeForm({ type, onClose }: { type?: EventType; onClose: () => void }): JSX.Element {
  const qc = useQueryClient();
  const [code, setCode] = useState(type?.code ?? '');
  const [description, setDescription] = useState(type?.description ?? '');
  const [granularity, setGranularity] = useState(type?.granularity ?? 'EACH');
  const [schema, setSchema] = useState(JSON.stringify(type?.schema ?? SAMPLE_SCHEMA, null, 2));
  const [parseError, setParseError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (parsed: object) => {
      const changed = type && JSON.stringify(parsed) !== JSON.stringify(type.schema);
      return type
        ? api(`/event-types/${type.code}`, { method: 'PATCH', body: { description, granularity, ...(changed ? { schema: parsed } : {}) } })
        : api('/event-types', { method: 'POST', body: { code, description, granularity, schema: parsed } });
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['event-types'] }); onClose(); },
  });
  return (
    <Modal title={type ? `Edit ${type.code}` : 'New event type'} description={type ? 'Changing the schema creates a new version; events keep the version they arrived with.' : 'Events of this type arrive at POST /api/v1/events/{code}.'} onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          try {
            const parsed = JSON.parse(schema) as object;
            setParseError(null);
            save.mutate(parsed);
          } catch (err) {
            setParseError(`The schema is not valid JSON: ${(err as Error).message}`);
          }
        }}
      >
        {(parseError || save.error) && <Alert tone="critical">{parseError ?? errorText(save.error)}</Alert>}
        {!type && <Field label="Code" required hint="Dotted, lower case: pos.daily_sales_summary, his.patient_charges"><input className={inputClass} value={code} onChange={(e) => setCode(e.target.value.trim())} required pattern="[a-z][a-z0-9_]*(\.[a-z0-9_]+)+" /></Field>}
        <Field label="Description"><input className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} /></Field>
        <Field label="Posting" hint="Daily rolls a day's events (per branch) into one summary journal — typical for POS and HIS volumes.">
          <select className={inputClass} value={granularity} onChange={(e) => setGranularity(e.target.value as 'EACH' | 'DAILY')}>
            <option value="EACH">One journal per event</option>
            <option value="DAILY">One summary journal per day and branch</option>
          </select>
        </Field>
        <Field label="Payload JSON Schema" required hint="Fields, types, required and enums. Payloads that fail go to the exception queue.">
          <textarea className={`${inputClass} font-mono text-xs`} rows={14} value={schema} onChange={(e) => setSchema(e.target.value)} spellCheck={false} />
        </Field>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={save.isPending}>Save</Button>
        </div>
      </form>
    </Modal>
  );
}

export function EventTypesPage(): JSX.Element {
  const { can } = useAuth();
  const qc = useQueryClient();
  const list = useEventTypes();
  const [editing, setEditing] = useState<EventType | 'new' | null>(null);
  const toggle = useMutation({ mutationFn: (t: EventType) => api(`/event-types/${t.code}`, { method: 'PATCH', body: { active: !t.active } }), onSuccess: () => void qc.invalidateQueries({ queryKey: ['event-types'] }) });
  return (
    <>
      <PageHeader title="Event Types" description="What outside systems can send: a code, a payload schema, and how it posts." actions={can('events.define') && <Button onClick={() => setEditing('new')}><Plus aria-hidden="true" className="h-4 w-4" /> New event type</Button>} />
      {list.isLoading && <Skeleton className="h-40 w-full" />}
      {list.data?.length === 0 && <Card><EmptyState title="No event types" description="Define one, e.g. pos.daily_sales_summary, then write a posting rule for it." /></Card>}
      {list.data && list.data.length > 0 && (
        <Card>
          <TableWrap>
            <thead><tr><Th>Event type</Th><Th>Posting</Th><Th>Schema</Th><Th>Events</Th><Th> </Th></tr></thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {list.data.map((t) => (
                <tr key={t.id} className={t.active ? '' : 'opacity-60'}>
                  <Td><code className="text-sm font-semibold">{t.code}</code><span className="block text-xs text-slate-500">{t.description}</span></Td>
                  <Td>{t.granularity === 'DAILY' ? 'Daily summary' : 'Each event'}</Td>
                  <Td>v{t.currentVersion}</Td>
                  <Td><div className="flex flex-wrap gap-1">{Object.entries(t.events).map(([s, n]) => <StatusChip key={s} tone={EVENT_TONE[s] ?? 'neutral'}>{n} {s.toLowerCase()}</StatusChip>)}{!Object.keys(t.events).length && <span className="text-xs text-slate-500">None yet</span>}</div></Td>
                  <Td>
                    {can('events.define') && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditing(t)}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => toggle.mutate(t)}>{t.active ? 'Deactivate' : 'Activate'}</Button>
                      </div>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}
      {editing && <EventTypeForm type={editing === 'new' ? undefined : editing} onClose={() => setEditing(null)} />}
    </>
  );
}
